import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import type { RootState } from '@store/types';
import { DashboardScope, DashboardResultType, DashboardResultOrder } from '@api/domain/types';
import type { DashboardResult } from '@api/domain/types';

import { DashboardOverviewDateRange, DashboardDateRangeSelection, DashboardState } from './types';
import {
  fetchDashboardOverall,
  fetchDashboardPerScopeValueWorst,
  fetchDashboardPerScopeValueBest,
  fetchDashboardPerScopeValueList,
} from './thunks';
import { computeDashboardDateRange } from '@v2/pages/dashboard/modules/overview/components/DashboardOverview/helpers/computeDashboardDateRange';

const LIST_PAGE_SIZE = 20;

const getResultId = (result: DashboardResult): string => {
  const r = result as DashboardResult & {
    machineId?: string;
    customerId?: string;
    partId?: string;
    ncFileId?: string;
  };
  return r.machineId ?? r.customerId ?? r.partId ?? r.ncFileId ?? '';
};

const initialRange = computeDashboardDateRange(DashboardOverviewDateRange.SEVEN_DAYS) ?? { from: '', to: '' };

const initialState: DashboardState = {
  dateRangeSelection: {
    dateRange: DashboardOverviewDateRange.SEVEN_DAYS,
    from: initialRange.from,
    to: initialRange.to,
  },
  scope: DashboardScope.Machine,
  resultType: DashboardResultType.PaidVersusActual,
  listOrderBy: DashboardResultOrder.WorstToBest,
  overall: {
    data: null,
    loading: false,
    error: null,
  },
  perScopeValueWorst: {
    data: null,
    loading: false,
    error: null,
  },
  perScopeValueBest: {
    data: null,
    loading: false,
    error: null,
  },
  perScopeValueList: {
    items: [],
    pageNumber: 0,
    totalCount: null,
    hasMore: false,
    loading: false,
    error: null,
  },
};

const dashboardSlice = createSlice({
  name: 'dashboard',
  initialState,
  reducers: {
    resetDashboard: () => initialState,
    setDashboardDateRangeSelection: (state, action: PayloadAction<DashboardDateRangeSelection>) => {
      state.dateRangeSelection = action.payload;
    },
    setDashboardScope: (state, action: PayloadAction<DashboardScope>) => {
      state.scope = action.payload;
    },
    setDashboardResultType: (state, action: PayloadAction<DashboardResultType>) => {
      state.resultType = action.payload;
    },
    setDashboardListOrderBy: (state, action: PayloadAction<DashboardResultOrder>) => {
      state.listOrderBy = action.payload;
    },
    resetDashboardPerScopeValueList: (state) => {
      state.perScopeValueList = initialState.perScopeValueList;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchDashboardOverall.pending, (state) => {
        state.overall.loading = true;
        state.overall.error = null;
      })
      .addCase(fetchDashboardOverall.fulfilled, (state, action) => {
        state.overall.loading = false;
        state.overall.data = action.payload ?? null;
      })
      .addCase(fetchDashboardOverall.rejected, (state, action) => {
        state.overall.loading = false;
        state.overall.error = action.error.message ?? 'Failed to load dashboard overall';
      })
      .addCase(fetchDashboardPerScopeValueWorst.pending, (state) => {
        state.perScopeValueWorst.loading = true;
        state.perScopeValueWorst.error = null;
      })
      .addCase(fetchDashboardPerScopeValueWorst.fulfilled, (state, action) => {
        state.perScopeValueWorst.loading = false;
        state.perScopeValueWorst.data = action.payload ?? null;
      })
      .addCase(fetchDashboardPerScopeValueWorst.rejected, (state, action) => {
        state.perScopeValueWorst.loading = false;
        state.perScopeValueWorst.error = action.error.message ?? 'Failed to load worst per-scope values';
      })
      .addCase(fetchDashboardPerScopeValueBest.pending, (state) => {
        state.perScopeValueBest.loading = true;
        state.perScopeValueBest.error = null;
      })
      .addCase(fetchDashboardPerScopeValueBest.fulfilled, (state, action) => {
        state.perScopeValueBest.loading = false;
        state.perScopeValueBest.data = action.payload ?? null;
      })
      .addCase(fetchDashboardPerScopeValueBest.rejected, (state, action) => {
        state.perScopeValueBest.loading = false;
        state.perScopeValueBest.error = action.error.message ?? 'Failed to load best per-scope values';
      })
      .addCase(fetchDashboardPerScopeValueList.pending, (state) => {
        state.perScopeValueList.loading = true;
        state.perScopeValueList.error = null;
      })
      .addCase(fetchDashboardPerScopeValueList.fulfilled, (state, action) => {
        state.perScopeValueList.loading = false;
        const { response, append, pageNumber } = action.payload;
        const pageItems: DashboardResult[] = response?.results ?? [];

        if (!append) {
          state.perScopeValueList.items = pageItems;
        } else {
          const seen = new Set<string>();
          const merged: DashboardResult[] = [];
          for (const item of [...state.perScopeValueList.items, ...pageItems]) {
            const key = `${item.$type}:${getResultId(item)}`;
            if (seen.has(key)) continue;
            seen.add(key);
            merged.push(item);
          }
          state.perScopeValueList.items = merged;
        }

        state.perScopeValueList.pageNumber = pageNumber;
        state.perScopeValueList.totalCount = null;
        state.perScopeValueList.hasMore = pageItems.length === LIST_PAGE_SIZE;
      })
      .addCase(fetchDashboardPerScopeValueList.rejected, (state, action) => {
        state.perScopeValueList.loading = false;
        state.perScopeValueList.error = action.error.message ?? 'Failed to load dashboard list';
      });
  },
});

export const {
  resetDashboard,
  setDashboardDateRangeSelection,
  setDashboardScope,
  setDashboardResultType,
  setDashboardListOrderBy,
  resetDashboardPerScopeValueList,
} = dashboardSlice.actions;

export const selectDashboardOverall = (state: RootState) => state.dashboard.overall;
export const selectDashboardPerScopeValueWorst = (state: RootState) => state.dashboard.perScopeValueWorst;
export const selectDashboardPerScopeValueBest = (state: RootState) => state.dashboard.perScopeValueBest;
export const selectDashboardDateRangeSelection = (state: RootState) => state.dashboard.dateRangeSelection;
export const selectDashboardScope = (state: RootState) => state.dashboard.scope;
export const selectDashboardResultType = (state: RootState) => state.dashboard.resultType;
export const selectDashboardListOrderBy = (state: RootState) => state.dashboard.listOrderBy;
export const selectDashboardPerScopeValueList = (state: RootState) => state.dashboard.perScopeValueList;

export default dashboardSlice.reducer;
